import { TTL_COD_MINUTE } from './otp-cod';

/**
 * Minimul din care ecranul de introducere a codului se poate reface dupa o REINCARCARE.
 *
 * 🔴 De ce exista: starea ecranului traia doar in `location.state`. Omul iese din browser ca sa
 * citeasca emailul, iar la intoarcere un WebView de Android (Facebook, Gmail, WhatsApp) reincarca
 * pagina — `location.state` dispare, ecranul spune „Nu știm pentru ce cont e codul", iar omul cere
 * INCA un cod. Exact valul de recereri pe care il masuram.
 *
 * 🔴 Ce se tine si unde:
 *   - doar identificatorul (propria lui adresa), fluxul, originea si clipele cererii — NIMIC din
 *     formularul de inscriere (nume, telefon), niciun cod, niciun mesaj;
 *   - in `sessionStorage`, NU in `localStorage`: moare cu fila, nu ramane pe un telefon imprumutat,
 *     si nu se vede din alte file. Nu ajunge in loguri si nu ajunge in telemetrie;
 *   - cel mult `TTL_COD_MINUTE` de la cerere: dupa atat codul e oricum expirat, iar un ecran
 *     restaurat ar cere un cod care nu mai poate reusi;
 *   - se sterge la reusita si la «Schimbă».
 *
 * Partea pura (`serializeazaStare`, `citesteStare`) e testata; cele trei functii de jos sunt doar
 * `try/catch` in jurul stocarii, care poate arunca (mod privat, stocare blocata, cota plina).
 */

export const CHEIE_STARE_COD = 'ecotic-otp-verificare';

const VALABILITATE_MS = TTL_COD_MINUTE * 60_000;

export interface StareCodPastrata {
    identificator: string;
    flux: 'inregistrare' | 'autentificare';
    /** Clipa (ms) a ultimei cereri de cod: din ea porneste si numaratoarea de retrimitere. */
    cerutLa: number;
    origine?: 'autentificare';
    codNouDupa?: number;
}

/** Forma minima a lui `Storage`, ca testele sa nu aiba nevoie de un browser. */
export interface StocareSimpla {
    getItem(cheie: string): string | null;
    setItem(cheie: string, valoare: string): void;
    removeItem(cheie: string): void;
}

function esteClipa(v: unknown): v is number {
    return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/** Textul de pus in stocare; `null` cand nu e nimic de tinut minte (fara identificator / clipa). */
export function serializeazaStare(stare: StareCodPastrata): string | null {
    const identificator = String(stare.identificator ?? '').trim();
    if (!identificator || !esteClipa(stare.cerutLa)) return null;
    return JSON.stringify({
        identificator,
        flux: stare.flux === 'inregistrare' ? 'inregistrare' : 'autentificare',
        cerutLa: stare.cerutLa,
        ...(stare.origine === 'autentificare' ? { origine: 'autentificare' } : {}),
        ...(esteClipa(stare.codNouDupa) ? { codNouDupa: stare.codNouDupa } : {}),
    });
}

/**
 * Starea citita din stocare, sau `null` daca nu e de folosit: text lipsa ori stricat, forma
 * gresita, cerere mai veche de `TTL_COD_MINUTE`, sau o clipa „din viitor" (ceas dat inapoi —
 * altfel o intrare n-ar mai expira niciodata).
 *
 * `acum` e parametru, ca la `stareRetrimitere`: cu `Date.now()` inauntru n-ar fi testabila.
 */
export function citesteStare(text: string | null | undefined, acum: number): StareCodPastrata | null {
    if (!text || !Number.isFinite(acum)) return null;
    let brut: unknown;
    try {
        brut = JSON.parse(text);
    } catch {
        return null;
    }
    if (typeof brut !== 'object' || brut === null) return null;
    const o = brut as Record<string, unknown>;

    const identificator = typeof o.identificator === 'string' ? o.identificator.trim() : '';
    if (!identificator || !esteClipa(o.cerutLa)) return null;
    if (o.cerutLa > acum || acum - o.cerutLa >= VALABILITATE_MS) return null;

    return {
        identificator,
        flux: o.flux === 'inregistrare' ? 'inregistrare' : 'autentificare',
        cerutLa: o.cerutLa,
        ...(o.origine === 'autentificare' ? { origine: 'autentificare' as const } : {}),
        ...(esteClipa(o.codNouDupa) ? { codNouDupa: o.codNouDupa } : {}),
    };
}

/** `sessionStorage`, sau `null` cand nu exista ori accesul arunca (mod privat, iframe blocat). */
function stocareaSesiunii(): StocareSimpla | null {
    try {
        return typeof window !== 'undefined' ? window.sessionStorage : null;
    } catch {
        return null;
    }
}

export function pastreazaStareCod(stare: StareCodPastrata, stocare: StocareSimpla | null = stocareaSesiunii()): void {
    try {
        const text = serializeazaStare(stare);
        if (text === null) stocare?.removeItem(CHEIE_STARE_COD);
        else stocare?.setItem(CHEIE_STARE_COD, text);
    } catch {
        // Fara stocare, ecranul merge ca inainte: doar nu se mai reface dupa o reincarcare.
    }
}

export function restaureazaStareCod(acum: number, stocare: StocareSimpla | null = stocareaSesiunii()): StareCodPastrata | null {
    try {
        const stare = citesteStare(stocare?.getItem(CHEIE_STARE_COD), acum);
        // O intrare expirata sau stricata nu are de ce sa ramana: e adresa omului.
        if (stare === null) stocare?.removeItem(CHEIE_STARE_COD);
        return stare;
    } catch {
        return null;
    }
}

export function stergeStareCod(stocare: StocareSimpla | null = stocareaSesiunii()): void {
    try {
        stocare?.removeItem(CHEIE_STARE_COD);
    } catch {
        // Nimic de facut: intrarea expira oricum singura, la citire.
    }
}
